/**
 * Teleprompter copy — the board's script as text a prompter app can step
 * through, one line at a time.
 *
 * A prompter advances a PARAGRAPH at a time and reads a blank line as the
 * paragraph break. Kola's script has no blank lines between its blocks (one
 * row = one block = one line), so pasted straight in it arrives as a single
 * wall the operator has to scroll by hand. Putting a blank line between every
 * line gives each one its own cue, which is what makes the prompter
 * steppable.
 *
 * This is a COPY-TIME projection and nothing more: the board is not touched,
 * and the blank lines exist only on the clipboard. The storyboard keeps the
 * script exactly as written — see BoardScreen, which reads the cells and
 * never writes them back.
 *
 * Pure — no clipboard, no DOM — so the menu item and the tests share one
 * definition of what "spaced out" means.
 */

/** What separates two lines on the clipboard: one blank line between them. */
const LINE_GAP = "\n\n";

/**
 * The prompter cues in `blocks`, in order — blank entries dropped, trailing
 * whitespace trimmed off each.
 *
 * A block is normally one line, but it need not be: Shift+Enter puts newlines
 * INSIDE a block (see ScriptView), and every one of those is a line break the
 * reader will pause at. So blocks are flattened to lines rather than kept
 * whole — the unit a prompter steps through is the line, not the row.
 *
 * Blank lines go because they are exactly what is about to be re-added:
 * keeping them would double the gap wherever the writer already left one, and
 * would make the spacing depend on how the script happened to be typed.
 *
 * CRLF and CR are accepted alongside LF — a script pasted into Kola carries
 * whatever line endings its source used.
 */
export function scriptLines(blocks: readonly string[]): string[] {
  return blocks
    .flatMap((block) => block.replaceAll("\r\n", "\n").replaceAll("\r", "\n").split("\n"))
    .map((line) => line.trimEnd())
    .filter((line) => line !== "");
}

/**
 * The clipboard text for `blocks`: every line separated from the next by a
 * blank line. Empty string when there is nothing to copy, which the caller
 * reports rather than putting an empty clipboard in the user's way.
 *
 * Idempotent — text that has already been through here comes back unchanged,
 * because the blank lines it added are dropped again before the new ones go
 * in.
 */
export function scriptToTeleprompterText(blocks: readonly string[]): string {
  return scriptLines(blocks).join(LINE_GAP);
}
